import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { SelectItem, TmdxSelect, RadioGroup, Radio } from '@ds';
import { AdminFormRow } from '@ui';
import { EXTRA_SHIFTS_AMOUNT, YES_NO_OPTIONS } from '@lp/features/admin/forms/config';
import { EmployeeFormValues } from '@/types';

type ClinicalSpecialistExtraShiftsRowProps = {
  title: string;
  isReadOnly?: boolean;
  booleanFieldName: keyof EmployeeFormValues;
  countFieldName: keyof EmployeeFormValues;
};

export const ClinicalSpecialistExtraShiftsRow = ({
  title,
  isReadOnly,
  booleanFieldName,
  countFieldName,
}: ClinicalSpecialistExtraShiftsRowProps) => {
  const { control, setValue } = useFormContext<EmployeeFormValues>();
  const booleanValue = useWatch({
    control,
    name: booleanFieldName,
  });

  const handleRadioChange = (val: string, onChange: (value: boolean) => void) => {
    const isTrue = val === 'true';
    onChange(isTrue);

    if (!isTrue) {
      setValue(countFieldName, 0);
    }
  };

  return (
    <AdminFormRow title={title} isRequired>
      <div className="flex items-center">
        <Controller
          name={booleanFieldName}
          control={control}
          render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
            <RadioGroup
              className="gap-8 shrink-0"
              orientation="horizontal"
              value={String(value)}
              isInvalid={invalid}
              isDisabled={isReadOnly}
              errorMessage={error?.message}
              onValueChange={(val) => handleRadioChange(val, onChange)}
              {...field}
            >
              {YES_NO_OPTIONS.map((option) => (
                <Radio className="w-20 max-w-none" key={String(option.value)} value={String(option.value)} size="lg">
                  {option.name}
                </Radio>
              ))}
            </RadioGroup>
          )}
        />
        <Controller
          name={countFieldName}
          control={control}
          render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
            <TmdxSelect
              className="max-w-20"
              aria-label={`${title} count`}
              selectedKeys={value !== undefined && value !== null ? [String(value)] : []}
              isInvalid={invalid}
              errorMessage={error?.message}
              disallowEmptySelection
              isDisabled={!booleanValue || isReadOnly}
              onSelectionChange={(key) => {
                const selectedKey = Array.from(key)[0] as string;
                onChange(parseInt(selectedKey, 10));
              }}
              {...field}
            >
              {EXTRA_SHIFTS_AMOUNT.map((amount) => (
                <SelectItem key={amount}>{amount}</SelectItem>
              ))}
            </TmdxSelect>
          )}
        />
      </div>
    </AdminFormRow>
  );
};
